/**
 * Главная, вариант Г (docs/design/home-2026-10-13): шапка-карточка, лента
 * «Проверь сегодня», лента платежей до прихода и лист «Все платежи».
 *
 * Числа считает главная (`Today` в app.tsx) — здесь только вид. Правила вида:
 * всё, что показывает число, нажимается и ведёт туда, где его разбирают;
 * проблема стоит в строке, к которой относится, а не отдельной фразой.
 */
import { useRef, useState, type ReactNode } from 'react';
import { ZERO, add, formatMoney, type IncomeOutlook, type Money, type PlainDate } from '@skoka/core';
import { Icon } from './icon.js';
import { SheetWrap } from './sheet.js';
import { plural, pluralWord } from './text.js';
import { formatAmountInput } from './store.js';
import type { SpendFocus } from './spend-steps.js';

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const WEEK = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const dayOf = (d: string) => Number(d.slice(8, 10));
const monthIx = (d: string) => Number(d.slice(5, 7)) - 1;
export const longDate = (d: string): string => `${dayOf(d)} ${MONTHS[monthIx(d)]}`;
const shortDate = (d: string): string => `${dayOf(d)} ${SHORT[monthIx(d)]}`;
const weekday = (d: string): string => WEEK[new Date(`${d}T12:00:00Z`).getUTCDay()]!;
/** Рубли без копеек, вниз: в шапке семизначная сумма с копейками не помещается, а «можно тратить» нельзя завышать. */
const rubles = (m: Money): string => formatMoney((Math.floor(m / 100) * 100) as Money);

// ───────────────────────────────────────────────────────────── шапка

export interface HeroStrip {
  tone: 'ok' | 'warn' | 'bad' | 'info';
  text: ReactNode;
  onClick?: () => void;
}

export function HeroCard(p: {
  today: PlainDate;
  horizonDate: PlainDate | null;
  daysToHorizon: number;
  amount: Money;
  /** «≈ 2 480 ₽ в день» — только когда есть дата прихода и деньги. */
  perDay: Money | null;
  /** Подпись вместо «в день»: запас хода или чего не хватает. */
  sub: string | null;
  strips: readonly HeroStrip[];
  onAccounts: Money;
  reserved: Money;
  free: Money;
  /** «Как посчитано» — открыт на числе, которое нажали. */
  onSpend: (focus: SpendFocus) => void;
}) {
  const tile = (label: string, value: Money, focus: SpendFocus, aria: string) => (
    <button className="gh-tile" onClick={() => p.onSpend(focus)} aria-label={aria}>
      <span className="gh-tlabel">{label}<Icon name="chevron-right" size={10} /></span>
      <b className="num">{rubles(value)}</b>
    </button>
  );
  return (
    <section className="gh">
      <div className="gh-top">
        <span>Сегодня, {longDate(p.today)}</span>
        {p.horizonDate && p.daysToHorizon > 0 && (
          <span className="gh-chip">{plural(p.daysToHorizon, 'день', 'дня', 'дней')} до прихода</span>
        )}
      </div>
      <h1 className="gh-title">{p.horizonDate ? `Можно тратить до ${longDate(p.horizonDate)}` : 'Можно тратить'}</h1>
      <div className="gh-row">
        <div className="gh-sum num">{rubles(p.amount)}</div>
        <button className="gh-go" onClick={() => p.onSpend('total')} aria-label="Как посчитано «Можно тратить»">
          <Icon name="arrow-right" size={20} />
        </button>
      </div>
      {p.perDay !== null && <div className="gh-day num">≈ {formatMoney(p.perDay)} в день</div>}
      {p.sub && <div className="gh-sub">{p.sub}</div>}
      {p.strips.map((s, k) => {
        const body = (<><i aria-hidden="true" /><span>{s.text}</span>{s.onClick && <Icon name="chevron-right" size={11} />}</>);
        return s.onClick
          ? <button key={k} className={`gh-strip ${s.tone}`} onClick={s.onClick}>{body}</button>
          : <div key={k} className={`gh-strip ${s.tone}`}>{body}</div>;
      })}
      <div className="gh-tiles">
        {tile('На счетах', p.onAccounts, 'acc', 'На счетах — как посчитано')}
        {tile('Отложено', p.reserved, 'res', 'Отложено — что и под что')}
        {tile('Не отложено', p.free, 'free', 'Не отложено — как посчитано')}
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────── «Проверь сегодня»

export interface HomeTodo {
  key: string;
  /** Метка над названием: «срок был 6 октября», «на счёте меньше». */
  pill: string;
  tone: 'warn' | 'bad' | 'ok' | 'info';
  title: string;
  amount?: Money;
  note: string;
  /** Нет ответа — карточка только сообщает (приход, обещанный дважды). */
  main?: { label: string; run: () => void };
  alt?: { label: string; run: () => void };
  /** Третий, редкий ответ — ссылкой под кнопками: «Отменить ожидание». */
  extra?: { label: string; run: () => void };
}

/** Карточки дел — листаются вбок; одно дело — одна карточка во всю ширину. */
export function TodoRail({ todos }: { todos: readonly HomeTodo[] }) {
  const rail = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(0);
  if (todos.length === 0) return null;
  const onScroll = () => {
    const el = rail.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (!el || !first) return;
    setAt(Math.min(todos.length - 1, Math.round(el.scrollLeft / (first.offsetWidth + 12))));
  };
  return (
    <>
      <div className="gsec">
        <h2>Проверь сегодня</h2>
        <span className="gcount"><i aria-hidden="true" />{plural(todos.length, 'вопрос', 'вопроса', 'вопросов')}</span>
      </div>
      <div className={`grail${todos.length === 1 ? ' one' : ''}`} ref={rail} onScroll={onScroll}
        aria-label={todos.length > 1 ? 'Проверь сегодня — листай вбок' : 'Проверь сегодня'}>
        {todos.map((t) => (
          <div className="gcard" key={t.key}>
            <span className={`gpill ${t.tone}`}>{t.pill}</span>
            <b className="gcard-title">{t.title}</b>
            {t.amount !== undefined && <span className="gcard-sum num">{formatMoney(t.amount)}</span>}
            <span className="gcard-note">{t.note}</span>
            {(t.main || t.alt) && (
              <div className="gcard-acts">
                {t.main && <button className="btn pri" onClick={t.main.run}>{t.main.label}</button>}
                {t.alt && <button className="btn" onClick={t.alt.run}>{t.alt.label}</button>}
              </div>
            )}
            {t.extra && <button className="lnk gcard-extra" onClick={t.extra.run}>{t.extra.label}</button>}
          </div>
        ))}
      </div>
      {todos.length > 1 && (
        <div className="gdots" aria-hidden="true">
          {todos.map((t, k) => <i key={t.key} className={k === at ? 'on' : ''} />)}
        </div>
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────── платежи

export interface PayRowView {
  key: string;
  label: string;
  date: PlainDate;
  amount: Money;
  status: string;
  /** Что уже внесено — перед статусом, своим цветом: «1 000 ₽ внесено заранее». */
  paid?: string;
  tone: 'ok' | 'bad' | 'dim';
  /** После ближайшего прихода — платится из него. */
  after: boolean;
  /** Сколько не хватает до прихода; ноль — отложено или из прихода. */
  short: Money;
  run?: () => void;
}

/** Одна дата — один узел ленты: в один день бывает несколько платежей. */
const byDate = (rows: readonly PayRowView[]): PayRowView[][] => {
  const out: PayRowView[][] = [];
  for (const r of rows) {
    const last = out[out.length - 1];
    if (last && last[0]!.date === r.date) last.push(r); else out.push([r]);
  }
  return out;
};

/** «25–30 окт», «30 окт – 2 ноя». */
const span = (a: string, b: string): string => (a === b ? shortDate(a)
  : monthIx(a) === monthIx(b) ? `${dayOf(a)}–${dayOf(b)} ${SHORT[monthIx(b)]}` : `${shortDate(a)} – ${shortDate(b)}`);

const Dot = ({ rows }: { rows: readonly PayRowView[] }) => {
  const bad = rows.some((r) => r.tone === 'bad');
  const ok = rows.every((r) => r.tone === 'ok');
  return (
    <span className={`gtl-dot ${bad ? 'bad' : ok ? 'ok' : 'dim'}`} aria-hidden="true">
      {ok && <Icon name="check" size={10} />}
    </span>
  );
};

const PayLine = ({ r, onPick }: { r: PayRowView; onPick?: () => void }) => {
  const body = (
    <>
      <span className="gtl-nm"><b>{r.label}</b><span className={`pst ${r.tone}`}>{r.paid && <em className="pst-paid">{r.paid} · </em>}{r.status}</span></span>
      <span className="gtl-am num">{formatMoney(r.amount)}</span>
      {r.run && <Icon name="chevron-right" size={11} />}
    </>
  );
  return r.run
    ? <button className="gtl-line" onClick={() => { onPick?.(); r.run!(); }}>{body}</button>
    : <div className="gtl-line">{body}</div>;
};

const Node = ({ rows, onPick }: { rows: readonly PayRowView[]; onPick?: () => void }) => (
  <div className="gtl-node">
    <span className="gtl-date"><b className="num">{dayOf(rows[0]!.date)}</b><span>{SHORT[monthIx(rows[0]!.date)]}</span></span>
    <Dot rows={rows} />
    <span className="gtl-body">{rows.map((r) => <PayLine key={r.key} r={r} {...(onPick ? { onPick } : {})} />)}</span>
  </div>
);

const Finish = ({ date, label, note, onClick }: { date: PlainDate; label: string; note: string; onClick?: () => void }) => (
  <div className="gtl-node">
    <span className="gtl-date"><b className="num">{dayOf(date)}</b><span>{SHORT[monthIx(date)]}</span></span>
    <span className="gtl-dot pay" aria-hidden="true"><i /></span>
    <button className="gtl-finish" onClick={onClick} disabled={!onClick}>
      <span className="gtl-nm"><b>{label}</b><span>{note}</span></span>
      {onClick && <Icon name="chevron-right" size={11} />}
    </button>
  </div>
);

/** Сколько узлов ленты видно на главной; остальное — свёрнутой строкой и в листе. */
const SHOWN = 3;

/**
 * Лента платежей до прихода на главной — ограниченная: ближайшие даты,
 * свёрнутая строка «ещё N» и приход в конце. Прокрутки внутри главной нет:
 * палец застревал бы в ленте, а спрятанные платежи никто не видит.
 */
export function PayTimeline(p: {
  rows: readonly PayRowView[];
  horizonDate: PlainDate | null;
  incomeLabel: string;
  onAll: () => void;
}) {
  const before = p.rows.filter((r) => !r.after);
  const list = before.length > 0 ? before : p.rows;
  if (list.length === 0) return null;
  const nodes = byDate(list);
  const shown = nodes.slice(0, SHOWN);
  const rest = nodes.slice(SHOWN).flat();
  const restShort = rest.filter((r) => r.short > ZERO).length;
  const covered = list.filter((r) => r.tone === 'ok').length;
  const total = add(...list.map((r) => r.amount));
  return (
    <>
      <div className="gsec">
        <h2>Ближайшие платежи</h2>
        <button className="glink" onClick={p.onAll}>Все {p.rows.length}</button>
      </div>
      <section className="gtl">
        <div className="gtl-head">
          <span>{p.horizonDate && before.length > 0 ? `до ${longDate(p.horizonDate)}` : 'дальше'} · <b className="num">{formatMoney(total)}</b></span>
          <span className="gtl-bar" aria-label={`${covered} из ${list.length} отложено`}>
            {list.map((r) => <i key={r.key} className={r.tone === 'ok' ? 'ok' : 'no'} />)}
            <em>{covered} из {list.length} отложено</em>
          </span>
        </div>
        <div className="gtl-list">
          {shown.map((rows) => <Node key={rows[0]!.key} rows={rows} />)}
          {rest.length > 0 && (
            <button className="gtl-node gtl-more" onClick={p.onAll}>
              <span className="gtl-date"><span>{span(rest[0]!.date, rest[rest.length - 1]!.date)}</span></span>
              <span className="gtl-dots" aria-hidden="true"><i /><i /><i /></span>
              <span className="gtl-fold">
                <span className="gtl-nm"><b>Ещё {plural(rest.length, 'платёж', 'платежа', 'платежей')}</b>
                  {restShort > 0 && <span className="pst bad">{restShort === 1 ? 'в одном не хватает' : `в ${restShort} не хватает`}</span>}</span>
                <span className="gtl-am num">{formatMoney(add(...rest.map((r) => r.amount)))}</span>
              </span>
            </button>
          )}
          {p.horizonDate && before.length > 0 && (
            <Finish date={p.horizonDate} label={p.incomeLabel} note="дальше платежи — из прихода" onClick={p.onAll} />
          )}
        </div>
      </section>
    </>
  );
}

type Filter = 'all' | 'short' | 'before';

/** Неделя с понедельника: «Эта неделя», «19–25 окт», «26 окт – 1 ноя». */
function weekLabel(d: PlainDate, today: PlainDate): string {
  const mon = (x: string) => {
    const t = new Date(`${x}T12:00:00Z`);
    t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7));
    return t.toISOString().slice(0, 10);
  };
  const a = mon(d);
  if (a === mon(today)) return 'Эта неделя';
  const end = new Date(`${a}T12:00:00Z`); end.setUTCDate(end.getUTCDate() + 6);
  const b = end.toISOString().slice(0, 10);
  return monthIx(a) === monthIx(b) ? `${dayOf(a)}–${dayOf(b)} ${SHORT[monthIx(b)]}` : `${shortDate(a)} – ${shortDate(b)}`;
}

/** Все платежи до конца отрезка «После прихода» — по неделям, с приходом посередине. */
export function PaymentsSheet(p: {
  rows: readonly PayRowView[];
  today: PlainDate;
  horizonDate: PlainDate | null;
  incomeLabel: string;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState<Filter>('all');
  const before = p.rows.filter((r) => !r.after);
  const short = p.rows.filter((r) => r.short > ZERO);
  const list = filter === 'short' ? short : filter === 'before' ? before : p.rows;
  const sum = (xs: readonly PayRowView[]) => add(...xs.map((r) => r.amount));
  const shortSum = add(...before.map((r) => r.short));
  const groups: { label: string; rows: PayRowView[]; payday?: boolean }[] = [];
  let payday = false;
  for (const r of list) {
    if (r.after && !payday && p.horizonDate) {
      groups.push({ label: 'Приход', rows: [], payday: true });
      groups.push({ label: 'После прихода · из прихода', rows: [] });
      payday = true;
    }
    const label = r.after ? 'После прихода · из прихода' : weekLabel(r.date, p.today);
    const last = groups[groups.length - 1];
    if (last && !last.payday && last.label === label) last.rows.push(r); else groups.push({ label, rows: [r] });
  }
  const chip = (f: Filter, label: string) => (
    <button className={`chip${filter === f ? ' on' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>{label}</button>
  );
  return (
    <SheetWrap title="Платежи" onClose={p.onClose}>
      <div className="gps">
        {p.horizonDate && before.length > 0 && (
          <>
            <div className="gps-cap">До прихода {longDate(p.horizonDate)}</div>
            <div className="gps-sum">
              <div><span>Всего</span><b className="num">{formatMoney(sum(before))}</b></div>
              <div className="ok"><span>Отложено</span><b className="num">{formatMoney(sum(before.filter((r) => r.tone === 'ok')))}</b></div>
              <div className={shortSum > ZERO ? 'bad' : ''}><span>Не хватает</span><b className="num">{formatMoney(shortSum)}</b></div>
            </div>
          </>
        )}
        <div className="chips gps-chips">
          {chip('all', `Все · ${p.rows.length}`)}
          {short.length > 0 && chip('short', `Не хватает · ${short.length}`)}
          {p.horizonDate && before.length > 0 && before.length < p.rows.length && chip('before', `До прихода · ${before.length}`)}
        </div>
        <div className="gtl gps-list">
          {groups.map((g, k) => (g.payday
            ? <Finish key={`pay:${k}`} date={p.horizonDate!} label={p.incomeLabel} note="дальше платежи — из прихода" />
            : g.rows.length === 0 ? null : (
              <div key={`${g.label}:${k}`}>
                <div className="gps-week">{g.label}</div>
                <div className="gtl-list">
                  {byDate(g.rows).map((rows) => <SheetNode key={rows[0]!.key} rows={rows} onPick={p.onClose} />)}
                </div>
              </div>
            )))}
          {list.length === 0 && <div className="note">Таких платежей нет.</div>}
        </div>
      </div>
    </SheetWrap>
  );
}

/** Узел в листе — с днём недели: по нему планируют неделю. */
const SheetNode = ({ rows, onPick }: { rows: readonly PayRowView[]; onPick: () => void }) => (
  <div className="gtl-node">
    <span className="gtl-date"><b className="num">{dayOf(rows[0]!.date)}</b><span>{SHORT[monthIx(rows[0]!.date)]}, {weekday(rows[0]!.date)}</span></span>
    <Dot rows={rows} />
    <span className="gtl-body">{rows.map((r) => <PayLine key={r.key} r={r} onPick={onPick} />)}</span>
  </div>
);

/** «за 4 мес», «по твоей оценке» — откуда «обычно в месяц». */
export function usualSource(u: { source: 'history' | 'estimate' | 'pinned' | 'none'; months: number }): string | null {
  if (u.source === 'history') return `в среднем за ${u.months} ${pluralWord(u.months, 'месяц', 'месяца', 'месяцев')}`;
  if (u.source === 'estimate') return 'по твоей оценке';
  if (u.source === 'pinned') return 'закреплено тобой';
  return null;
}

/**
 * «После прихода» — отрезок от ближайшего прихода до следующего: что придёт,
 * какие платежи из него, сколько уйдёт на жизнь и что останется. Тот же
 * расчёт, что у плитки на главной (`incomeOutlook`).
 */
export function AfterIncomeSheet({ outlook, onClose }: { outlook: IncomeOutlook; onClose: () => void }) {
  const line = (l: { label: string; date: PlainDate; amount: Money }, k: number, sign: string) => (
    <div className="gaf-line" key={`${l.label}:${l.date}:${k}`}>
      <span className="gaf-date num">{shortDate(l.date)}</span>
      <span className="gaf-nm">{l.label}</span>
      <span className="num">{sign} {formatMoney(l.amount)}</span>
    </div>
  );
  return (
    <SheetWrap title="После прихода" onClose={onClose}
      tip="Прогноз, а не деньги: пока приход не случился, в «Можно тратить» он не входит. Отложенное уже сейчас отсюда второй раз не вычитается.">
      <div className="gps">
        <div className="gps-cap">{longDate(outlook.from)} – {longDate(outlook.to)}</div>
        <div className="gaf-block">
          <div className="gaf-head"><b>Придёт{outlook.variable ? ', по нижней оценке' : ''}</b><b className="num pos">+ {formatMoney(outlook.income)}</b></div>
          {[...outlook.incomeLines].sort((a, b) => a.date.localeCompare(b.date)).map((l, k) => line(l, k, '+'))}
        </div>
        <div className="gaf-block">
          <div className="gaf-head"><b>Платежи</b><b className="num">− {formatMoney(outlook.payments)}</b></div>
          {[...outlook.paymentLines].sort((a, b) => a.date.localeCompare(b.date)).map((l, k) => line(l, k, '−'))}
        </div>
        <div className="gaf-block">
          <div className="gaf-head"><b>Жизнь</b><b className="num">{outlook.living === null ? 'не задана' : `− ${formatMoney(outlook.living)}`}</b></div>
          <div className="note" style={{ marginTop: 2 }}>повседневные нужды и свободные траты — «обычно в месяц» на этот отрезок</div>
        </div>
        <div className="gaf-head gaf-total"><b>Останется</b>
          <b className={`num${outlook.left !== null && outlook.left < ZERO ? ' bad' : ''}`}>{outlook.left === null ? 'назови обычные траты' : `≈ ${formatMoney(outlook.left)}`}</b></div>
      </div>
    </SheetWrap>
  );
}

export interface ReserveTarget {
  obligationId: string;
  label: string;
  date: PlainDate;
  /** Сколько не отложено под эту дату. */
  need: Money;
  /** Счёт, с которого откладывать по умолчанию — стоит первым. */
  accountId: string;
  /** Почему он первый: «здесь уже конверт», «счёт платежа». */
  accountHint?: string;
}

/**
 * «Отложить» под платёж: со счёта платежа или с другого, где есть свободные
 * деньги. «Можно тратить» эти деньги уже не считает — конверт только не даёт
 * их потратить.
 */
export function ReserveSheet({ target, accounts, onSave, onClose, AmountField }: {
  target: ReserveTarget;
  accounts: readonly { id: string; name: string; free: Money }[];
  onSave: (accountId: string, amount: Money) => Promise<void>;
  onClose: () => void;
  AmountField: (p: { value: string; onChange: (v: string) => void }) => JSX.Element;
}) {
  const sorted = [...accounts].sort((a, b) => Number(b.id === target.accountId) - Number(a.id === target.accountId) || b.free - a.free);
  const first = sorted.find((a) => a.free > ZERO) ?? sorted[0];
  const [pick, setPick] = useState(first?.id ?? '');
  const fill = (id: string) => {
    const a = sorted.find((x) => x.id === id);
    const v = a ? Math.min(target.need, a.free) : 0;
    return v > 0 ? formatAmountInput(String(v / 100).replace('.', ',')) : '';
  };
  const [amount, setAmount] = useState(() => fill(first?.id ?? ''));
  const [busy, setBusy] = useState(false);
  const value = Math.round(Number(amount.replace(/[^\d,]/g, '').replace(',', '.')) * 100) as Money;
  const acc = sorted.find((a) => a.id === pick);
  return (
    <SheetWrap title={`Отложить на «${target.label}»`} onClose={onClose}>
      <div className="form">
        <div className="note" style={{ marginTop: 0 }}>{longDate(target.date)} · не отложено {formatMoney(target.need)}. «Можно тратить» эти деньги уже не считает — конверт не даст их потратить.</div>
        <div className="pick"><div className="k">С какого счёта</div></div>
        {sorted.map((a) => (
          <button key={a.id} className="li w" aria-pressed={pick === a.id} disabled={a.free <= ZERO}
            onClick={() => { setPick(a.id); setAmount(fill(a.id)); }}>
            <div className="nm"><b>{a.name}{a.id === target.accountId && target.accountHint ? ` · ${target.accountHint}` : ''}</b>
              <span>свободно {formatMoney(a.free)}</span></div>
            <span className="ch">{pick === a.id ? <Icon name="check" size={14} /> : null}</span>
          </button>
        ))}
        <label>Сколько отложить<AmountField value={amount} onChange={setAmount} /></label>
        <button className="btn pri" disabled={busy || !acc || value <= ZERO || value > (acc?.free ?? 0)}
          onClick={async () => { setBusy(true); await onSave(pick, value); setBusy(false); }}>
          {acc ? `Отложить ${formatMoney(value > ZERO ? value : ZERO)} с «${acc.name}»` : 'Выбери счёт'}
        </button>
        {acc && value > acc.free && <div className="note warn">На «{acc.name}» свободно только {formatMoney(acc.free)}.</div>}
      </div>
    </SheetWrap>
  );
}
